import React, { useEffect, useRef } from "react";
import { canAnimate, clamp, seeded } from "./motion";

const TILT = 0.36;
const land = (lat, lon) =>
  Math.sin(lon * 3 + Math.sin(lat * 5) * 1.4) * Math.cos(lat * 7 - lon * 2) +
    Math.sin(lon * 5 - lat * 3) * 0.55 +
    Math.cos(lon * 1.7 + lat * 11) * 0.25 >
  0.18;

// A dotted planet rising on the horizon. Work routes arc between its cities; drag it and it keeps spinning.
export default function PioneerHorizon() {
  const host = useRef(null);
  const canvasRef = useRef(null);
  useEffect(() => {
    const box = host.current;
    const canvas = canvasRef.current;
    if (!box || !canvas || !window.matchMedia) return undefined;
    const ctx = canvas.getContext?.("2d");
    if (!ctx) return undefined;
    const animate = canAnimate();
    let w = 0;
    let h = 0;
    let R = 0;
    let cap = 0;
    let points = [];
    let spin = 0.6;
    let speed = 0.0009;
    let raf = 0;
    let visible = false;
    let arcs = [];
    let last = 0;
    let lastSpawn = 0;
    let drag = null;
    const build = () => {
      const rect = box.getBoundingClientRect();
      w = rect.width;
      h = rect.height;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      R = w < 700 ? w * 1.45 : w * 0.82;
      cap = h * 0.8;
      const gap = w < 700 ? 9 : 12.5;
      const k = 1 - cap / R;
      const minLat = Math.max(0, Math.asin(clamp(k, -1, 1)) - TILT - 0.04);
      points = [];
      const random = seeded(5);
      for (let lat = minLat; lat < Math.PI / 2; lat += gap / R) {
        const ring = Math.max(1, Math.floor((2 * Math.PI * R * Math.cos(lat)) / gap));
        const shift = random() * Math.PI * 2;
        for (let n = 0; n < ring; n += 1) {
          const lon = ((n + (random() - 0.5) * 0.35) / ring) * Math.PI * 2 + shift;
          if (land(lat, lon)) points.push([Math.cos(lat) * Math.cos(lon), Math.sin(lat), Math.cos(lat) * Math.sin(lon)]);
        }
      }
    };
    const rotate = ([x, y, z]) => {
      const cs = Math.cos(spin);
      const sn = Math.sin(spin);
      const x1 = x * cs - z * sn;
      const z1 = x * sn + z * cs;
      const ct = Math.cos(TILT);
      const st = Math.sin(TILT);
      return [x1, y * ct - z1 * st, z1 * ct + y * st];
    };
    const project = ([x, y, z]) => [w / 2 + x * R, h - cap + R - y * R, z];
    const onScreen = (v) => {
      const [sx, sy, z] = project(rotate(v));
      return z > 0.05 && sx > 40 && sx < w - 40 && sy < h - 8 && sy > h - cap + 12 ? [sx, sy] : null;
    };
    const spawn = (t) => {
      if (arcs.length > 5 || t - lastSpawn < 650) return;
      lastSpawn = t;
      for (let tries = 0; tries < 20; tries += 1) {
        const a = points[Math.floor(Math.random() * points.length)];
        const b = points[Math.floor(Math.random() * points.length)];
        const pa = onScreen(a);
        const pb = onScreen(b);
        if (pa && pb && Math.hypot(pa[0] - pb[0], pa[1] - pb[1]) > 110 && Math.abs(pa[0] - pb[0]) < w * 0.45) {
          arcs.push({ a, b, born: t, life: 1700 + Math.random() * 900 });
          return;
        }
      }
    };
    const along = (a, b, s, lift) => {
      const dot = clamp(a[0] * b[0] + a[1] * b[1] + a[2] * b[2], -1, 1);
      const om = Math.acos(dot) || 1e-6;
      const k1 = Math.sin((1 - s) * om) / Math.sin(om);
      const k2 = Math.sin(s * om) / Math.sin(om);
      const r = 1 + Math.sin(Math.PI * s) * lift;
      return [(a[0] * k1 + b[0] * k2) * r, (a[1] * k1 + b[1] * k2) * r, (a[2] * k1 + b[2] * k2) * r];
    };
    const draw = (t) => {
      ctx.clearRect(0, 0, w, h);
      const cyc = h - cap + R;
      const glow = ctx.createRadialGradient(w / 2, cyc, R, w / 2, cyc, R * 1.07);
      glow.addColorStop(0, "rgba(25,25,25,0.06)");
      glow.addColorStop(1, "rgba(25,25,25,0)");
      ctx.fillStyle = glow;
      ctx.fillRect(0, 0, w, h);
      ctx.beginPath();
      ctx.arc(w / 2, cyc, R, 0, Math.PI * 2);
      ctx.fillStyle = "#ffffff";
      ctx.fill();
      ctx.strokeStyle = "rgba(25,25,25,0.22)";
      ctx.lineWidth = 1;
      ctx.stroke();
      const layers = [new Path2D(), new Path2D(), new Path2D(), new Path2D()];
      const size = w < 700 ? 1.6 : 2;
      for (const v of points) {
        const [x, y, z] = rotate(v);
        if (z <= 0) continue;
        const sy = cyc - y * R;
        if (sy > h + 2) continue;
        const sx = w / 2 + x * R;
        if (sx < -4 || sx > w + 4) continue;
        const depth = Math.min(3, Math.floor(z * 4.2));
        layers[depth].rect(sx, sy, size, size);
      }
      [0.16, 0.28, 0.42, 0.58].forEach((alpha, i) => {
        ctx.fillStyle = `rgba(25,25,25,${alpha})`;
        ctx.fill(layers[i]);
      });
      if (!animate) return;
      arcs = arcs.filter((arc) => t - arc.born < arc.life + 600);
      ctx.fillStyle = "#191919";
      for (const arc of arcs) {
        const q = clamp((t - arc.born) / arc.life);
        const lift = 0.06 + Math.acos(clamp(arc.a[0] * arc.b[0] + arc.a[1] * arc.b[1] + arc.a[2] * arc.b[2], -1, 1)) * 0.35;
        const tail = Math.max(0, q - 0.4);
        ctx.beginPath();
        for (let k = 0; k <= 22; k += 1) {
          const [sx, sy] = project(rotate(along(arc.a, arc.b, tail + ((q - tail) * k) / 22, lift)));
          if (k) ctx.lineTo(sx, sy);
          else ctx.moveTo(sx, sy);
        }
        ctx.strokeStyle = "rgba(25,25,25,0.55)";
        ctx.lineWidth = 1.2;
        ctx.stroke();
        const [hx, hy] = project(rotate(along(arc.a, arc.b, q, lift)));
        const [ax, ay] = project(rotate(arc.a));
        ctx.beginPath();
        ctx.arc(ax, ay, 2.4, 0, Math.PI * 2);
        ctx.fill();
        if (q < 1) {
          ctx.beginPath();
          ctx.arc(hx, hy, 2.6, 0, Math.PI * 2);
          ctx.fill();
        } else {
          const ping = clamp((t - arc.born - arc.life) / 600);
          ctx.beginPath();
          ctx.arc(hx, hy, 2.5 + ping * 15, 0, Math.PI * 2);
          ctx.strokeStyle = `rgba(25,25,25,${0.6 * (1 - ping)})`;
          ctx.stroke();
          ctx.beginPath();
          ctx.arc(hx, hy, 2.4, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    };
    const frame = (t) => {
      raf = visible ? requestAnimationFrame(frame) : 0;
      const dt = Math.min(48, t - (last || t));
      last = t;
      if (!drag) {
        speed += (0.00009 - speed) * 0.02;
        spin += speed * dt;
      }
      spawn(t);
      draw(t);
    };
    const onDown = (e) => {
      if (!animate || (e.pointerType === "mouse" && e.button !== 0)) return;
      drag = { x: e.clientX, t: performance.now(), v: 0 };
      canvas.setPointerCapture?.(e.pointerId);
      box.classList.add("is-dragging");
    };
    const onMove = (e) => {
      if (!drag) return;
      const now = performance.now();
      const dx = e.clientX - drag.x;
      spin += dx / R;
      drag.v = dx / R / Math.max(8, now - drag.t);
      drag.x = e.clientX;
      drag.t = now;
    };
    const onUp = () => {
      if (!drag) return;
      speed = clamp(drag.v, -0.006, 0.006);
      drag = null;
      box.classList.remove("is-dragging");
    };
    build();
    draw(0);
    const observer = window.IntersectionObserver
      ? new IntersectionObserver(([entry]) => {
          visible = entry.isIntersecting && animate;
          last = 0;
          if (visible && !raf) raf = requestAnimationFrame(frame);
        })
      : null;
    observer?.observe(box);
    const resizer = window.ResizeObserver
      ? new ResizeObserver(() => {
          if (Math.abs(box.getBoundingClientRect().width - w) > 2) {
            build();
            draw(performance.now());
          }
        })
      : null;
    resizer?.observe(box);
    canvas.addEventListener("pointerdown", onDown);
    canvas.addEventListener("pointermove", onMove);
    canvas.addEventListener("pointerup", onUp);
    canvas.addEventListener("pointercancel", onUp);
    return () => {
      cancelAnimationFrame(raf);
      observer?.disconnect();
      resizer?.disconnect();
      canvas.removeEventListener("pointerdown", onDown);
      canvas.removeEventListener("pointermove", onMove);
      canvas.removeEventListener("pointerup", onUp);
      canvas.removeEventListener("pointercancel", onUp);
    };
  }, []);
  return (
    <div ref={host} className="bm-horizon" aria-hidden="true">
      <canvas ref={canvasRef} />
    </div>
  );
}
